import { Stack, Typography } from '@mui/material';
import { Star, Target } from 'lucide-react';

// Umbrales disjuntos: evitan que una misma especialidad compita por ambas listas.
const STRENGTH_MIN_SCORE = 70;
const IMPROVEMENT_MAX_SCORE = 60;

function normalizeItems(items = []) {
	return (items || [])
		.map((item) => ({
			name: item?.specialty_name || item?.name || item?.label || 'Sin nombre',
			score: Number(item?.score_percentage ?? item?.percentage ?? 0),
		}))
		.filter((item) => item.name);
}

function InsightRow({ title, items, tone = 'success', icon }) {
	const isSuccess = tone === 'success';

	return (
		<div className="flex min-w-0 items-start gap-4">
			<div
				className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
				style={{
					backgroundColor: isSuccess ? '#e9f9ef' : '#fff1f1',
					border: `1px solid ${isSuccess ? '#b9e9c8' : '#ffd0cc'}`,
					boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.65)',
				}}
			>
				{icon}
			</div>

			<div className="flex-1 min-w-0 ml-2">
				<Typography sx={{ fontSize: { xs: 14, sm: 16 }, lineHeight: 1.25, fontWeight: 600, color: '#475569' }}>
					{title}
				</Typography>

				<Stack spacing={1.5} sx={{ mt: 1.25, width: '100%', minWidth: 0 }}>
					{items.length === 0 ? (
						<Typography sx={{ color: '#94a3b8', fontSize: 13, fontWeight: 400 }}>Sin datos disponibles</Typography>
					) : (
						<ul className="list-none p-0 m-0 space-y-3">
							{items.map((item) => (
								<li key={`${title}-${item.name}`} className="flex items-center gap-3 py-0.5">
									<span
										className="inline-block h-4 w-4 rounded-full flex-shrink-0"
										style={{
											backgroundColor: isSuccess ? '#22c55e' : '#f97316',
											boxShadow: isSuccess
												? '0 0 0 4px rgba(34,197,94,0.16)'
												: '0 0 0 4px rgba(249,115,22,0.18)',
										}}
									/>
									<Typography
										sx={{
											fontSize: 14,
											fontWeight: 600,
											lineHeight: 1.4,
											color: isSuccess ? '#166534' : '#b91c1c',
										}}
									>
										{item.name}
									</Typography>
								</li>
							))}
						</ul>
					)}
				</Stack>
			</div>
		</div>
	);
}

export default function CardFortalezas({ resultsPayload, title = 'Fortalezas y areas de mejora', maxItems = 3 }) {
	const improvementAreas = normalizeItems(resultsPayload?.improvement_areas)
		.filter((item) => item.score <= IMPROVEMENT_MAX_SCORE)
		.sort((a, b) => a.score - b.score)
		.slice(0, maxItems);

	const strengths = normalizeItems(resultsPayload?.strengths)
		.filter((item) => item.score >= STRENGTH_MIN_SCORE)
		.sort((a, b) => b.score - a.score)
		.slice(0, maxItems);

	return (
		<div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 overflow-hidden shadow-lg rounded">
			<div className="rounded-t mb-0 px-4 py-3 border-0">
				<div className="flex flex-wrap items-center">
					<div className="relative w-full px-4 max-w-full flex-grow flex-1">
						<h3 className="font-semibold text-base text-blueGray-700">{title}</h3>
					</div>
				</div>
			</div>

			<div className="px-6 pb-6 pt-2">
				<Stack spacing={3}>
				<InsightRow
					title="Fortalezas"
					items={strengths}
					tone="success"
					icon={<Star size={28} color="#22c55e" fill="#22c55e" strokeWidth={1.8} />}
				/>

				<div className="border-t border-blueGray-100" />

				<InsightRow
					title="Areas de mejora"
					items={improvementAreas}
					tone="danger"
					icon={<Target size={28} color="#f97316" strokeWidth={2.4} />}
				/>
				</Stack>
			</div>
		</div>
	);
}
